import { notifyError } from "@/lib/notify-error";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery } from "convex/react";
import { api } from "@constructionautomation/backend";
import { FileText, Settings, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  JobListLoadMore,
  JobListPage,
  jobListDateColumn,
  type JobListColumn,
} from "@/components/layout";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { InlineEditNameCell } from "@/components/inline-edit-name-cell";
import { RowAction, RowActionsMenu } from "@/components/row-actions-menu";
import { Button } from "@/components/ui/button";
import { JobStatusBadge } from "@/components/job-status-badge";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useInfiniteScroll } from "@/lib/use-infinite-scroll";
import { useProject } from "@/components/projects";
import { RequiresCapability, useOnEditAccessLost } from "@/lib/capabilities";

export const Route = createFileRoute("/specPilot/")({
  component: DashboardPage,
});

const PAGE_SIZE = 40;

export function DashboardPage() {
  const navigate = useAppNavigate();
  const { projectId, isLoading: isProjectLoading } = useProject();
  const jobsQuery = usePaginatedQuery(
    api.specPilot.submittals.listWithTableCountsPaginated,
    projectId ? { projectId } : "skip",
    { initialNumItems: PAGE_SIZE }
  );
  const deleteJob = useMutation(api.specPilot.submittals.remove);
  const updateName = useMutation(api.specPilot.submittals.updateName);
  // The target outlives `isDeleteDialogOpen` so the dialog copy stays put while it animates out.
  const [jobPendingDelete, setJobPendingDelete] = useState<JobListItem | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  useOnEditAccessLost(() => setIsDeleteDialogOpen(false));

  const jobs = jobsQuery.results;
  const isLoadingFirstPage = jobsQuery.status === "LoadingFirstPage" && jobs.length === 0;
  const loadMoreRef = useInfiniteScroll(
    jobsQuery.status === "CanLoadMore",
    jobsQuery.loadMore,
    PAGE_SIZE
  );

  const handleDeleteJob = async (submittalId: string) => {
    try {
      await deleteJob({
        submittalId: submittalId as Parameters<typeof deleteJob>[0]["submittalId"],
      });
      toast.success("SpecPilot job deleted successfully");
    } catch (error) {
      notifyError(error, "Failed to delete SpecPilot job");
      console.error("Delete error:", error);
    }
  };

  const handleUpdateName = async (submittalId: string, name: string) => {
    try {
      await updateName({
        submittalId: submittalId as Parameters<typeof updateName>[0]["submittalId"],
        name,
      });
      toast.success("Name updated");
    } catch (error) {
      notifyError(error, "Failed to update name");
      console.error(error);
      throw error;
    }
  };

  const handleNewSpecPilot = () => {
    navigate({ to: "/specPilot/new", title: "New SpecPilot Job" });
  };

  const handleManageTemplates = () => {
    navigate({ to: "/specPilot/templates", title: "Manage Templates" });
  };

  // Built per render: `@/components/layout` and this route import each other through the
  // workspace route manifest, so module-level calls into the layout barrel can run too early.
  const columns: JobListColumn<JobListItem>[] = [
    { id: "status", header: "Status", cell: (job) => <JobStatusBadge status={job.status} /> },
    { id: "tables", header: "Tables Found", variant: "number", cell: (job) => job.tableCount },
    jobListDateColumn("Created", (job) => job.createdAt),
  ];

  return (
    <JobListPage
      title="SpecPilot"
      newAction={{ label: "New SpecPilot Job", onClick: handleNewSpecPilot }}
      secondaryActions={
        <Button variant="outline" size="sm" onClick={handleManageTemplates}>
          <Settings />
          Manage Templates
        </Button>
      }
      emptyState={{
        icon: FileText,
        title: "No SpecPilot jobs yet",
        description: "Create a job to extract submittals from this project's specs.",
      }}
      isLoading={isProjectLoading || isLoadingFirstPage}
      rows={jobs}
      getRowKey={(job) => job._id}
      getRowName={(job) => job.name}
      getRowLink={(job) => ({
        to: "/specPilot/$jobId",
        params: { jobId: job._id },
        title: job.name,
      })}
      renderName={(job) => (
        <InlineEditNameCell value={job.name} onSave={(name) => handleUpdateName(job._id, name)} />
      )}
      columns={columns}
      renderActions={(job) => (
        <RequiresCapability mode="hide">
          <RowActionsMenu label={`Actions for ${job.name}`}>
            <RowAction
              icon={Trash2}
              destructive
              onSelect={() => {
                setJobPendingDelete(job);
                setIsDeleteDialogOpen(true);
              }}
            >
              Delete
            </RowAction>
          </RowActionsMenu>
        </RequiresCapability>
      )}
      hasMore={jobsQuery.status !== "Exhausted"}
      footer={
        <JobListLoadMore
          sentinelRef={loadMoreRef}
          isLoadingMore={jobsQuery.status === "LoadingMore"}
        />
      }
    >
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete SpecPilot Job"
        description={`Are you sure you want to delete "${jobPendingDelete?.name ?? ""}"? This will permanently delete all extracted tables and generated submittals associated with this job.`}
        onConfirm={() =>
          jobPendingDelete ? handleDeleteJob(jobPendingDelete._id) : undefined
        }
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </JobListPage>
  );
}

interface JobListItem {
  _id: string;
  name: string;
  status: "pending" | "processing" | "ready_for_review" | "completed" | "failed";
  tableCount?: number;
  createdAt: number;
}
